import { ScriptLine } from './Types'
import { SceneId } from './Scenes'
import { PromptType } from './PromptDef'
import { CharacterId } from './Characters'

export enum EpisodeStatus {
  PROCESSED = 'processed',
  ERROR = 'error',
  PROCESSING = 'processing',
  FLAGGED = 'flagged',
  ABORTED = 'aborted',
}

export interface EpisodeTitle {
  text: string
  voicefile?: string
}

export interface EpisodeSettingsUsed {
  ai_type: string
  model: string
  temperature: number
}

// ordered steps of the episode generation pipeline (see VnCreator.ts),
// used to compute an overall progress percentage
export enum EpisodeProgressStep {
  WRITING_SCRIPT = 'writing_script',
  DETERMINING_CHARACTERS = 'determining_characters',
  DETERMINING_EMOTIONS = 'determining_emotions',
  DETERMINING_SCENE = 'determining_scene',
  DETERMINING_TITLE = 'determining_title',
  SYNTHESIZING_VOICE = 'synthesizing_voice',
}

export const EPISODE_PROGRESS_STEP_ORDER: EpisodeProgressStep[] = [
  EpisodeProgressStep.WRITING_SCRIPT,
  EpisodeProgressStep.DETERMINING_CHARACTERS,
  EpisodeProgressStep.DETERMINING_EMOTIONS,
  EpisodeProgressStep.DETERMINING_SCENE,
  EpisodeProgressStep.DETERMINING_TITLE,
  EpisodeProgressStep.SYNTHESIZING_VOICE,
]

export const EPISODE_PROGRESS_STEP_LABELS: Record<EpisodeProgressStep, string> = {
  [EpisodeProgressStep.WRITING_SCRIPT]: 'Writing script',
  [EpisodeProgressStep.DETERMINING_CHARACTERS]: 'Designing characters',
  [EpisodeProgressStep.DETERMINING_EMOTIONS]: 'Determining emotions',
  [EpisodeProgressStep.DETERMINING_SCENE]: 'Determining scene',
  [EpisodeProgressStep.DETERMINING_TITLE]: 'Writing title',
  [EpisodeProgressStep.SYNTHESIZING_VOICE]: 'Synthesizing voice',
}

// mdi icon names (used by the frontend step indicator)
export const EPISODE_PROGRESS_STEP_ICONS: Record<EpisodeProgressStep, string> = {
  [EpisodeProgressStep.WRITING_SCRIPT]: 'mdi-script-text-outline',
  [EpisodeProgressStep.DETERMINING_CHARACTERS]: 'mdi-account-group-outline',
  [EpisodeProgressStep.DETERMINING_EMOTIONS]: 'mdi-emoticon-outline',
  [EpisodeProgressStep.DETERMINING_SCENE]: 'mdi-image-frame',
  [EpisodeProgressStep.DETERMINING_TITLE]: 'mdi-format-title',
  [EpisodeProgressStep.SYNTHESIZING_VOICE]: 'mdi-microphone-outline',
}

// short 1-word labels shown under each icon in the frontend step indicator
export const EPISODE_PROGRESS_STEP_SHORT_LABELS: Record<EpisodeProgressStep, string> = {
  [EpisodeProgressStep.WRITING_SCRIPT]: 'Script',
  [EpisodeProgressStep.DETERMINING_CHARACTERS]: 'Cast',
  [EpisodeProgressStep.DETERMINING_EMOTIONS]: 'Emotions',
  [EpisodeProgressStep.DETERMINING_SCENE]: 'Scene',
  [EpisodeProgressStep.DETERMINING_TITLE]: 'Title',
  [EpisodeProgressStep.SYNTHESIZING_VOICE]: 'Voice',
}

export interface EpisodeProgress {
  step: EpisodeProgressStep
  // current/total are only set when the step has a countable sub-progress
  // (e.g. "line 5 of 20"), otherwise both are null
  current: number | null
  total: number | null
}

// 1-based index of the current step among EPISODE_PROGRESS_STEP_ORDER, or null
// if there is no progress info (yet) / the step is unknown
export const episodeProgressStepNumber = (progress: EpisodeProgress | null): number | null => {
  if (!progress) {
    return null
  }
  const stepIdx = EPISODE_PROGRESS_STEP_ORDER.indexOf(progress.step)
  return stepIdx === -1 ? null : stepIdx + 1
}

// overall completion percentage (0-100) across the whole pipeline, or null
// if there is no progress info (yet) / the step is unknown
export const episodeProgressPercent = (progress: EpisodeProgress | null): number | null => {
  if (!progress) {
    return null
  }
  const stepIdx = EPISODE_PROGRESS_STEP_ORDER.indexOf(progress.step)
  if (stepIdx === -1) {
    return null
  }
  const stepCount = EPISODE_PROGRESS_STEP_ORDER.length
  const stepFraction = (progress.total && progress.current !== null)
    ? Math.min(progress.current / progress.total, 1)
    : 0
  const percent = ((stepIdx + stepFraction) / stepCount) * 100
  return Math.max(0, Math.min(100, Math.round(percent)))
}

export interface EpisodesRow {
  id: number
  episode_id: string
  played: number
  created: string // json date string
  creation_log: null // deprecated
  status: EpisodeStatus
  prompt_topic: string | null
  prompt_username: string | null
  prompt_usercolor: string | null
  prompt_type: PromptType | null
  prompt_poll_id: number | null
  prompt_prequel_episode_id: number | null
  scene_id: SceneId | null
  title: EpisodeTitle | null
  script: ScriptLine[] | null
  character_ids: CharacterId[] | null
  last_played: string // json date string
  gpt_output: string | null
  settings_used: EpisodeSettingsUsed | null
  progress: EpisodeProgress | null
}
